// ./src/components/features/EventVendors/vendorUi.ts
//
// ROADMAP #170 — how vendor compliance READS on screen: a request's state, what needs a look, a calendar date, a person's
// name. One place, so the list, the vendor's page and the drawers say the same words.
import dayjs from "dayjs";

import { EventVendor, EventVendorPerson, EventVendorRequirement, VENDOR_EMAIL_STATE, VENDOR_REQUIREMENT_STATUS } from "@/utils/api";

export const REQUIREMENT_STATUS_LABEL: Record<string, string> = {
  [VENDOR_REQUIREMENT_STATUS.MISSING]: "Not received",
  [VENDOR_REQUIREMENT_STATUS.SUBMITTED]: "To review",
  [VENDOR_REQUIREMENT_STATUS.APPROVED]: "Approved",
  [VENDOR_REQUIREMENT_STATUS.RETURNED]: "Sent back",
};

/** Status colours are status, never brand (a returned one is the vendor's move, so orange, not red). */
export const REQUIREMENT_STATUS_COLOR: Record<string, string> = {
  [VENDOR_REQUIREMENT_STATUS.MISSING]: "gray",
  [VENDOR_REQUIREMENT_STATUS.SUBMITTED]: "blue",
  [VENDOR_REQUIREMENT_STATUS.APPROVED]: "green",
  [VENDOR_REQUIREMENT_STATUS.RETURNED]: "orange",
};

export const ATTENTION_LABEL: Record<string, string> = {
  overdue: "Overdue",
  expires: "Expires before the event",
};

/**
 * Whether the crew can upload a document as the answer to this request: only while nothing has been sent for it (review
 * 2026-10-20). ⚠ ONE gate for the review drawer's "Upload one they sent you" button AND the `…/documents/:id/upload` stage,
 * so Back after a save, or a typed address, cannot open an upload the button would not offer.
 */
export const requestTakesUpload = (r: EventVendorRequirement | null | undefined): boolean => !!r && !r.document;

/** A calendar date ('YYYY-MM-DD') as "Nov 1, 2026" — a date has no zone, so none is applied. */
export const formatVendorDay = (day?: string | null): string => (day ? dayjs(day).format("MMM D, YYYY") : "");

/**
 * #202 — an email's fate in the crew's words, and what to do about it. ⓘ "Opened" is a hint (see `VendorEmailDeliveryList`), and "sent" means the
 * provider has not reported back, never that it arrived.
 */
export const vendorEmailWords = (state?: string | null, event?: string | null): { label: string; color: string; help: string | null } => {
  if (state === VENDOR_EMAIL_STATE.OPENED) {
    return { label: "Opened", color: "teal", help: null };
  }

  if (state === VENDOR_EMAIL_STATE.DELIVERED) {
    return { label: "Delivered", color: "green", help: null };
  }

  if (state !== VENDOR_EMAIL_STATE.NOT_DELIVERED) {
    return { label: "Sent", color: "blue", help: "No report back from their mail server yet." };
  }

  switch (event) {
    case "bounce":
      return { label: "Bounced", color: "red", help: "Their mail server refused it. What came back says why: check the address with them, or add another person who gets the emails." };
    case "dropped":
      return { label: "Not sent", color: "red", help: "Our email provider would not send to this address, usually because it bounced before. Check it with them, or add another person." };
    case "blocked":
      return { label: "Blocked", color: "red", help: "Their mail server blocked it. Ask them to allow it, or use another address." };
    case "spamreport":
      return { label: "Marked as spam", color: "red", help: "This person reported it as spam. Contact them another way." };
    default:
      return { label: "Not delivered", color: "red", help: "It did not reach them. Check the address, or contact them another way." };
  }
};

/** A Mantine 8 date picker's value ('YYYY-MM-DD' string, or a Date from an older caller) as 'YYYY-MM-DD', or null. */
export const pickerDay = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  return value instanceof Date && !Number.isNaN(value.getTime()) ? dayjs(value).format("YYYY-MM-DD") : null;
};

export const personName = (p?: EventVendorPerson | null): string => [p?.firstName, p?.lastName].filter(Boolean).join(" ").trim();

/** "3 of 5 approved" — or what is missing when nothing is asked yet. */
export const progressLine = (vendor: EventVendor): string => {
  const c = vendor.counts;

  if (!c || c.total === 0) {
    return "No documents asked for yet";
  }

  return `${c.approved} of ${c.total} approved`;
};

/** #202 — what a vendor still owes, by name, for the list: "Insurance · General liability, Permit +2". */
export const owedLine = (vendor: EventVendor, shown = 2): string => {
  const owed = (vendor.requirements ?? []).filter(
    (r) => r.reviewStatus === VENDOR_REQUIREMENT_STATUS.MISSING || r.reviewStatus === VENDOR_REQUIREMENT_STATUS.RETURNED
  );

  if (owed.length === 0) {
    return "";
  }

  const names = owed.slice(0, shown).map((r) => r.name).join(", ");

  return owed.length > shown ? `${names} +${owed.length - shown}` : names;
};

/** Does this request have a document to look at? */
export const hasDocument = (r: EventVendorRequirement): boolean => !!r.document?.id;
